
import React, { useState, useEffect } from "react";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import Layout from "@/components/layout/Layout";
import FinanceiroTabs from "@/components/financeiro/FinanceiroTabs";
import FinanceiroHeader from "@/components/financeiro/FinanceiroHeader";
import TransacoesLista from "@/components/financeiro/TransacoesLista";
import DashboardFinanceiro from "@/components/financeiro/DashboardFinanceiro";
import CadastroTransacao from "@/components/financeiro/CadastroTransacao";
import { Transacao } from "@/types/financeiro";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "@/contexts/AuthContext";
import { toast, Toaster } from "sonner";

const FinanceiroFormPage = () => {
  const { user } = useAuth();
  const [transacoes, setTransacoes] = useState<Transacao[]>([]);
  const [transacaoParaEditar, setTransacaoParaEditar] = useState<Transacao | null>(null);
  const [tabAtiva, setTabAtiva] = useState("dashboard");

  const handleTabChange = (value: string) => {
    setTabAtiva(value);
    
    // Disparar evento personalizado para comunicação entre componentes
    const event = new CustomEvent('change-tab', {
      detail: { tab: value }
    });
    window.dispatchEvent(event);
    
    // Atualizar URL para refletir a aba selecionada
    const url = new URL(window.location.href);
    url.searchParams.set('tab', value);
    window.history.pushState({}, '', url);
  };
  
  // Get the URL parameters to see if a specific tab should be activated
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab) {
      setTabAtiva(tab);
    }
  }, []);

  const handleSaveTransacao = (transacao: Omit<Transacao, "id" | "criadoEm" | "criadoPor">) => {
    if (transacaoParaEditar) {
      // Editar transação existente
      const updatedTransacoes = transacoes.map(t => 
        t.id === transacaoParaEditar.id 
          ? { ...transacaoParaEditar, ...transacao } 
          : t
      );
      setTransacoes(updatedTransacoes);
      setTransacaoParaEditar(null);
      toast.success("Transação atualizada com sucesso!");
    } else {
      // Criar nova transação
      const novaTransacao: Transacao = {
        ...transacao,
        id: uuidv4(),
        criadoEm: new Date(),
        criadoPor: user?.nome || "Usuário"
      };
      setTransacoes([...transacoes, novaTransacao]);
      toast.success("Transação cadastrada com sucesso!");
    }
    setTabAtiva("lista");
  };

  const handleEditTransacao = (id: string) => {
    const transacao = transacoes.find(t => t.id === id);
    if (transacao) {
      setTransacaoParaEditar(transacao);
      setTabAtiva("cadastro");
    }
  };

  const handleDeleteTransacao = (id: string) => {
    setTransacoes(transacoes.filter(t => t.id !== id));
    toast.success("Transação excluída com sucesso!");
  };

  const handleCancelEdit = () => {
    setTransacaoParaEditar(null);
  };

  return (
    <Layout>
      <div className="container mx-auto p-4 md:p-6 max-w-7xl">
        <div className="flex flex-col mb-6">
          <div className="mb-4">
            <h1 className="text-2xl font-normal text-gray-700 mb-1">Financeiro</h1>
            <p className="text-sm text-gray-500 mt-1 opacity-75">
              Gerenciamento Financeiro
            </p>
          </div>
          <div className="w-full rounded-lg shadow-sm overflow-hidden">
            <FinanceiroTabs 
              tabAtiva={tabAtiva} 
              onChangeTab={handleTabChange} 
              variant="buttons"
              className="overflow-x-auto pb-2 md:pb-0" 
            />
          </div>
        </div>
        
        <Tabs value={tabAtiva} onValueChange={handleTabChange} className="w-full">          
          <TabsContent value="dashboard">
            <DashboardFinanceiro transacoes={transacoes} />
          </TabsContent>
          
          <TabsContent value="lista">
            <TransacoesLista 
              transacoes={transacoes} 
              onEdit={handleEditTransacao}
              onDelete={handleDeleteTransacao}
            />
          </TabsContent>
          
          <TabsContent value="cadastro">
            <CadastroTransacao 
              onSave={handleSaveTransacao}
              transacao={transacaoParaEditar}
              onCancel={handleCancelEdit}
            />
          </TabsContent>
        </Tabs>
        
        <Toaster position="top-right" />
      </div>
    </Layout>
  );
};

export default FinanceiroFormPage;
